import { required, TextInput, SelectInput, ReferenceInput, AutocompleteInput, maxLength, regex } from "react-admin";

import { Grid, Typography } from "@mui/material";
import { GenderOptionsId, PersonIdTypes, PmsOptions } from "utils/choices";
import { documentNumberValidator, nameCyrillicRegexValidator, validateSecondName } from "utils/validations";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import PersonIdInput from "./PersonIdInput";
import PrivateCustomImageInput from "./PrivateCustomImageInput";
import { administrationRequests } from "api/administrationApi";
import { CitizenshipIds } from "utils/constants";
import { tenants } from "utils/tenants";
import { settings } from "app";
import { getFile } from "server/components/DownloadFile";

// Keep a cleared name as "" instead of react-admin's default null. With null, ra-core's
// useApplyInputDefaultValues puts `defaultValue` straight back: its dirty check misses nested
// sources like "userInfo.firstNameBg", so the field could not be emptied and retyped.
// CreateTemplate turns empty optional names back into null when saving.
export const keepEmptyString = value => value;

const PersonalInfo = ({
  defaultBirthCountry,
  isDisable,
  userData,
  isKSK = false,
  notify,
  isPms = false,
  isPreparatory = false,
  isForeigner,
  setPersonalDocumentFrontId,
  setPersonalDocumentBackId,
  setIsDisable,
  setForeignerType,
  setPersonIdType,
  setPmsType,
}) => {
  const isMgu = settings.tenant === tenants.MGU;
  const hasEuOrNonEuCitizenship =
    userData?.userInfo?.citizenshipId === CitizenshipIds.EuForeigners ||
    userData?.userInfo?.citizenshipId === CitizenshipIds.NotEuForeigners;
  const frontDocumentId = userData?.personalDocument?.frontDocumentId;
  const backDocumentId = userData?.personalDocument?.backDocumentId;
  return (
    <Grid
      container
      spacing={2}
    >
      <Grid
        item
        xs={12}
        md={4}
      >
        <TextInput
          source="userInfo.firstNameBg"
          label="Име"
          defaultValue={userData?.userInfo?.firstNameBg}
          parse={keepEmptyString}
          validate={[required("Задължително"), nameCyrillicRegexValidator]}
          readOnly={isDisable}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <TextInput
          source="userInfo.secondNameBg"
          label="Презиме"
          defaultValue={userData?.userInfo?.secondNameBg || ""}
          parse={keepEmptyString}
          readOnly={isDisable}
          validate={[validateSecondName, nameCyrillicRegexValidator]}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <TextInput
          source="userInfo.lastNameBg"
          label="Фамилия"
          defaultValue={userData?.userInfo?.lastNameBg || ""}
          parse={keepEmptyString}
          validate={[required("Задължително"), nameCyrillicRegexValidator]}
          readOnly={isDisable}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={4}
      >
        <SelectInput
          defaultValue={userData?.userInfo?.personIdType || (isForeigner && !isMgu ? null : 1)}
          source="userInfo.personIdType"
          choices={isForeigner && !isMgu ? PersonIdTypes.filter(t => t.id !== 1) : PersonIdTypes}
          label="Тип идентификатор"
          validate={required("Задължително")}
          readOnly={isDisable || (!isForeigner && !isMgu)}
          onChange={e => setPersonIdType(e.target.value)}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={4}
      >
        <PersonIdInput
          isKSK={isKSK}
          isForeigner={isForeigner}
          isDisable={isDisable}
          userData={userData}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={4}
      >
        <TextInput
          source="ean"
          label="ЕАН"
          readOnly={isDisable}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={4}
      >
        <TextInput
          label="Телефон"
          source="userInfo.contactPhone"
          defaultValue={userData?.userInfo?.contactPhone}
          validate={regex(/^(359|0){1}[0-9]{9}$/, "Моля въведете валиден телефон")}
          readOnly={isDisable}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <TextInput
          source="email"
          label="Имейл"
          defaultValue={(isKSK ? userData?.email : userData?.userInfo?.email) || ""}
          validate={required("Задължително")}
          readOnly={isDisable}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <SelectInput
          label="Пол"
          source="userInfo.gender"
          choices={GenderOptionsId}
          validate={required("Задължително")}
          fullWidth
          defaultValue={userData?.userInfo?.gender}
          readOnly={isDisable}
        />
      </Grid>
      {isForeigner && !isKSK && (
        <Grid
          item
          xs={12}
          md={4}
        >
          <ReferenceInput
            reference={administrationRequests.getCitizenshipSelect()}
            source="userInfo.citizenshipId"
            filter={{ IsForeigner: true }}
          >
            <AutocompleteInput
              filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
              optionText="name"
              optionValue="id"
              defaultValue={hasEuOrNonEuCitizenship ? userData?.userInfo?.citizenshipId : null}
              onChange={e => setForeignerType(e)}
              validate={required("Задължително")}
              label="Тип Гражданство ЕС / Извън ЕС"
              readOnly={isDisable}
              fullWidth
            />
          </ReferenceInput>
        </Grid>
      )}
      <Grid
        item
        xs={12}
        md={4}
      >
        <ReferenceInput
          reference={administrationRequests.getCountriesSelect()}
          source="primaryCitizenshipCountryId"
        >
          <AutocompleteInput
            filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
            optionText="nameBg"
            defaultValue={!isForeigner ? defaultBirthCountry : null}
            validate={required("Задължително")}
            label="Гражданство"
            readOnly={isDisable}
            fullWidth
          />
        </ReferenceInput>
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <ReferenceInput
          reference={administrationRequests.getCountriesSelect()}
          source="secondaryCitizenshipCountryId"
        >
          <AutocompleteInput
            filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
            optionText="nameBg"
            label="Второ гражданство"
            readOnly={isDisable}
            fullWidth
          />
        </ReferenceInput>
      </Grid>
      {isPms && (
        <Grid
          item
          xs={12}
          md={4}
        >
          <TextInput
            source="studentIbanNumber"
            label="Банкова сметка"
            validate={maxLength(50)}
            readOnly={isDisable}
          />
        </Grid>
      )}
      {isForeigner && (
        <>
          <Grid
            item
            xs={12}
            md={4}
          >
            <TextInput
              source="personalDocument.number"
              label="Номер на документ за самоличност"
              validate={[maxLength(50), documentNumberValidator()]}
              readOnly={isDisable}
            />
          </Grid>
          {!isPreparatory && (
            <Grid
              item
              xs={12}
              md={4}
            >
              <CustomDateInput
                source="personalDocument.validUntil"
                label="Валиден до"
                readOnly={isDisable}
              />
            </Grid>
          )}

          {isPms && (
            <Grid
              item
              xs={12}
              md={4}
            >
              <SelectInput
                label="Тип ПМС"
                source="pmsType"
                choices={PmsOptions}
                validate={required("Задължително")}
                onChange={e => setPmsType(e.target.value)}
                fullWidth
                readOnly={isDisable}
              />
            </Grid>
          )}
          {!isPreparatory && (
            <>
              <Grid
                container
                spacing={2}
                p={2}
              >
                <Grid
                  item
                  xs={12}
                >
                  {frontDocumentId != null && (
                    <>
                      <Typography
                        color={"primary"}
                        fontSize={18}
                        py={2}
                      >
                        Прикачена снимка на документ за самоличност - предна част
                      </Typography>
                      <Typography
                        sx={{ textDecoration: "underline", cursor: "pointer" }}
                        fontSize={16}
                        fontWeight={"bold"}
                        mb={3}
                        onClick={() => getFile(frontDocumentId, notify)}
                      >{`image-${frontDocumentId}.jpeg`}</Typography>
                    </>
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <PrivateCustomImageInput
                    source="frontDocumentId"
                    name="frontDocumentId"
                    isDisable={isDisable}
                    setIsDisable={setIsDisable}
                    label={frontDocumentId != null ? "Смени снимка на документ за самоличност - предна част" : "Прикачи снимка на документ за самоличност - предна част"}
                    SubCategoryId={11}
                    setDataId={setPersonalDocumentFrontId}
                  />
                </Grid>
              </Grid>
              <Grid
                container
                spacing={2}
                p={2}
              >
               <Grid
                  item
                  xs={12}
                >
                  {backDocumentId != null && (
                    <>
                      <Typography
                        color={"primary"}
                        fontSize={18}
                        py={2}
                      >
                        Прикачена снимка на документ за самоличност - гръб
                      </Typography>
                      <Typography
                        sx={{ textDecoration: "underline", cursor: "pointer" }}
                        fontSize={16}
                        fontWeight={"bold"}
                        mb={3}
                        onClick={() => getFile(backDocumentId, notify)}
                      >{`image-${backDocumentId}.jpeg`}</Typography>
                    </>
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <PrivateCustomImageInput
                    source="backDocumentId"
                    name="backDocumentId"
                    isDisable={isDisable}
                    setIsDisable={setIsDisable}
                    label={backDocumentId != null ? "Смени снимка на документ за самоличност - гръб" : "Прикачи снимка на документ за самоличност - гръб"}
                    SubCategoryId={11}
                    setDataId={setPersonalDocumentBackId}
                  />
                </Grid>
              </Grid>
            </>
          )}
          {/* снимкИ на диплома - subCategoryIds = 10 - */}
        </>
      )}
    </Grid>
  );
};
export default PersonalInfo;
